/**
 * grass_layer.ts — the live grass layer: the ring meshes, their uniforms, the field texture
 * (2026-09-25, visual-realism R2).
 *
 * One Mesh per ring (grass_params.ts) under a RENDER-ANCHOR group, so the shader's local space IS
 * render space (anchor − origin = 0) and the lattice/field/clump offsets stay small integers. Per
 * frame `update()` moves the camera uniform, re-snaps each ring's lattice base, follows the anchor,
 * drives the field window (nearest tiles first, a time budget) and uploads the field texture when it
 * changed. No per-instance CPU work: a 60 k-blade meadow costs a handful of uniform writes.
 *
 * Shadows: blades RECEIVE (tree shade on a meadow is most of the picture) and never CAST (sub-pixel
 * casters, thousands per cascade — the groundcover rule). Opaque; not an MRT overlay.
 */
import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';
import { makeAnchorGroup } from '../local_origin.js';
import { disposeLater } from '../gpu_dispose.js';
import { GrassField, GRASS_FIELD_N, GRASS_FIELD_REACH_M, type GrassFieldSource } from './grass_field.js';
import { grassParamsFor, grassCounts, ringWeight, type GrassLayerTier, type GrassParams } from './grass_params.js';
import { bladeAt, bladeTemplate, cardTemplate, createBladeMaterial, createCardMaterial, ringBase, ringHashOffset, clumpOffset, ringUniforms, type GrassShared, type RingUniforms } from './grass_blades.js';

export interface GrassExternals {
  /** lib/wind.ts: the shared wind uniform node (x dirX, y dirZ, z strength, w time) and last frame's dt node. */
  wind: unknown; windDt: unknown;
  /** lib/foliage_trample.ts texture + bounds node (render space), or null. */
  trample: THREE.Texture | null; trampleBounds: unknown;
  /** SkyModule: sun direction (toward the sun, world) and night factor nodes. */
  sunDir: unknown; night: unknown;
}

export class GrassLayer {
  readonly field = new GrassField();
  private readonly anchor = makeAnchorGroup('anchor:grass');
  private readonly tex: THREE.DataTexture;
  private readonly shared: GrassShared;
  private meshes: Array<{ mesh: THREE.Mesh; ring: GrassParams['rings'][number]; ru: RingUniforms }> = [];
  private params: GrassParams;
  private uploaded = -1;
  private lastUploadMs = 0;
  private visible = true;
  readonly stats = { tier: 'off' as GrassLayerTier, rings: 0, instances: 0, blades: 0, cards: 0, uploads: 0, fieldMs: 0 };

  constructor(private readonly scene: THREE.Scene, tier: GrassLayerTier, ext: GrassExternals) {
    this.tex = new THREE.DataTexture(this.field.data, GRASS_FIELD_N, GRASS_FIELD_N, THREE.RGBAFormat, THREE.FloatType);
    this.tex.minFilter = THREE.NearestFilter; this.tex.magFilter = THREE.NearestFilter; this.tex.generateMipmaps = false;
    this.tex.wrapS = THREE.RepeatWrapping; this.tex.wrapT = THREE.RepeatWrapping;
    this.tex.colorSpace = THREE.NoColorSpace; this.tex.flipY = false;
    this.tex.needsUpdate = true;
    this.shared = {
      field: this.tex,
      fieldOff: uniform(new THREE.Vector2()),
      cam: uniform(new THREE.Vector3()),
      clump: uniform(new THREE.Vector4()),
      density: uniform(1),
      wind: ext.wind, windDt: ext.windDt,
      trample: ext.trample, trampleBounds: ext.trampleBounds,
      sunDir: ext.sunDir, night: ext.night,
      tint: uniform(new THREE.Vector3(1, 1, 1)),
      windMin: uniform(0),
    };
    this.anchor.group.name = 'grass-layer';
    scene.add(this.anchor.group);
    this.params = grassParamsFor(tier);
    this.build();
  }

  private build(): void {
    // deferred: the frame in flight may still draw them (the warp-teardown law)
    for (const m of this.meshes) { this.anchor.group.remove(m.mesh); disposeLater(m.mesh.geometry); disposeLater(m.mesh.material as THREE.Material); }
    this.meshes = [];
    for (const ring of this.params.rings) {
      const ru = ringUniforms();
      const n = ring.grid * ring.grid;
      const geo = ring.kind === 'blade' ? bladeTemplate(ring.segments, n) : cardTemplate(n);
      const mat = ring.kind === 'blade' ? createBladeMaterial(ring, this.shared, ru) : createCardMaterial(ring, this.shared, ru);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.name = `grass:${ring.kind}:${ring.segments}:${ring.grid}`;
      mesh.frustumCulled = false; // the layer surrounds the camera
      mesh.castShadow = false;    // the groundcover rule
      mesh.receiveShadow = true;
      mesh.matrixAutoUpdate = false; mesh.updateMatrix();
      mesh.visible = this.visible;
      this.anchor.group.add(mesh);
      this.meshes.push({ mesh, ring, ru });
    }
    const c = grassCounts(this.params);
    Object.assign(this.stats, { tier: this.params.tier, rings: this.params.rings.length, instances: c.instances, blades: c.blades, cards: c.cards });
  }

  /** Switch tier (rebuilds the ring meshes; the field is kept). */
  setTier(tier: GrassLayerTier): void {
    if (tier === this.params.tier) return;
    this.params = grassParamsFor(tier);
    this.build();
  }
  get tier(): GrassLayerTier { return this.params.tier; }
  /** The master density multiplier (probe lever / menu). */
  setDensity(v: number): void { (this.shared.density as { value: number }).value = Math.max(0, v); }
  /** Seasonal / dryness tint on the turf (linear multiplier). */
  setTint(r: number, g: number, b: number): void { (this.shared.tint as { value: THREE.Vector3 }).value.set(r, g, b); }
  /** Probe lever: a floor under the wind strength (0 = the weather's own). */
  setWindMin(v: number): void { (this.shared.windMin as { value: number }).value = Math.max(0, v); }
  setVisible(on: boolean): void { this.visible = on; for (const m of this.meshes) m.mesh.visible = on; }
  get isVisible(): boolean { return this.visible; }
  /** The ring meshes (probes / warm-up). */
  get objects(): THREE.Object3D[] { return this.meshes.map((m) => m.mesh); }

  /**
   * Per frame: `camAbs` is the camera's ABSOLUTE position, `anchor` the render anchor (whole metres).
   * `budgetMs` bounds the field's tile fills this frame.
   */
  update(camAbs: { x: number; y: number; z: number }, anchor: { x: number; z: number }, src: GrassFieldSource, budgetMs: number): void {
    const lx = camAbs.x - anchor.x, lz = camAbs.z - anchor.z;
    (this.shared.cam as { value: THREE.Vector3 }).value.set(lx, camAbs.y, lz);
    const [fx, fz] = GrassField.slotOffset(anchor.x, anchor.z);
    (this.shared.fieldOff as { value: THREE.Vector2 }).value.set(fx, fz);
    const c = clumpOffset(anchor.x, anchor.z);
    (this.shared.clump as { value: THREE.Vector4 }).value.set(c[0], c[1], c[2], c[3]);
    for (const m of this.meshes) {
      const [bx, bz] = ringBase(m.ring, lx, lz);
      (m.ru.base as { value: THREE.Vector2 }).value.set(bx, bz);
      const [hx, hz] = ringHashOffset(m.ring, anchor.x, anchor.z);
      (m.ru.hashOff as { value: THREE.Vector2 }).value.set(hx, hz);
    }
    if (this.meshes.length === 0) return;
    const t0 = performance.now();
    this.field.update(camAbs.x, camAbs.z, budgetMs, src);
    this.stats.fieldMs = performance.now() - t0;
    // Upload whenever the field changed: a window move re-marks slots that now stand for other tiles, and a
    // stale GPU copy would seat those blades on the old tile's ground for as long as it lags.
    if (this.field.version !== this.uploaded) {
      this.uploaded = this.field.version;
      this.tex.needsUpdate = true;
      this.lastUploadMs = t0;
      this.stats.uploads++;
    }
  }

  /**
   * CENSUS (probes): the blades each ring really draws around the camera, from the CPU mirrors of the
   * shader (bladeAt + the field's bilinear read + the ring band + the density lever), how many of those
   * stand inside the camera's frustum, and how far a kept blade's foot (the field height) sits from
   * `meshAt` (the rendered mesh) within 20 m.
   */
  census(camera: THREE.Camera, camAbs: { x: number; z: number }, anchor: { x: number; z: number }, meshAt: (x: number, z: number) => number): { rings: Array<{ kind: string; instances: number; kept: number; onScreen: number }>; footMaxErr: number; footSamples: number } {
    const frustum = new THREE.Frustum().setFromProjectionMatrix(new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    const p = new THREE.Vector3();
    const dens = (this.shared.density as { value: number }).value;
    const lx = camAbs.x - anchor.x, lz = camAbs.z - anchor.z;
    const rings: Array<{ kind: string; instances: number; kept: number; onScreen: number }> = [];
    let footMaxErr = 0, footSamples = 0;
    for (const m of this.meshes) {
      const r = m.ring; const n = r.grid * r.grid; let kept = 0, onScreen = 0;
      for (let i = 0; i < n; i++) {
        const b = bladeAt(i, r, anchor.x, anchor.z, lx, lz);
        const d = Math.hypot(b.x - camAbs.x, b.z - camAbs.z);
        if (d >= GRASS_FIELD_REACH_M) continue;
        const f = this.field.sampleAt(b.x, b.z);
        if (!f) continue;
        if (!(b.keepR < f.density * ringWeight(r, d) * r.keep * dens)) continue;
        kept++;
        // the frustum test in RENDER space (absolute − origin; the scene sits at −origin = −anchor)
        p.set(b.x - anchor.x, f.h + 0.2, b.z - anchor.z);
        if (frustum.containsPoint(p)) onScreen++;
        if (d < 20 && footSamples < 4000) { footSamples++; footMaxErr = Math.max(footMaxErr, Math.abs(f.h - meshAt(b.x, b.z))); }
      }
      rings.push({ kind: `${r.kind}${r.kind === 'blade' ? r.segments : ''}`, instances: n, kept, onScreen });
    }
    return { rings, footMaxErr, footSamples };
  }

  /** Forget the field (world change / teleport far away). */
  resetField(): void { this.field.clear(); }

  dispose(): void {
    for (const m of this.meshes) { this.anchor.group.remove(m.mesh); disposeLater(m.mesh.geometry); disposeLater(m.mesh.material as THREE.Material); }
    this.meshes = [];
    this.anchor.release();
    this.scene.remove(this.anchor.group);
    disposeLater(this.tex);
    void this.lastUploadMs;
  }
}
